import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { startPcmRecorder, PcmRecorderHandle } from '../lib/pcmRecorder';

const apiBaseUrl = (import.meta.env.VITE_GRAPHQL_HOST as string) || '';

interface StartOptions {
  authHeader?: string; // optional explicit Authorization header for /token
}

interface TranscriptionStreamState {
  partial: string;
  final: string | null;
  speechStopped: boolean;
  error: Error | null;
  isStreaming: boolean;
}

export function useTranscriptionStream() {
  const [state, setState] = useState<TranscriptionStreamState>({
    partial: '',
    final: null,
    speechStopped: false,
    error: null,
    isStreaming: false,
  });

  const wsRef = useRef<WebSocket | null>(null);
  const recorderRef = useRef<PcmRecorderHandle | null>(null);
  const finalResolverRef = useRef<((text: string) => void) | null>(null);
  const finalRejecterRef = useRef<((err: Error) => void) | null>(null);

  const cleanup = useCallback(async () => {
    try {
      await recorderRef.current?.stop();
    } catch {}
    recorderRef.current = null;
    try {
      wsRef.current?.close();
    } catch {}
    wsRef.current = null;
  }, []);

  const start = useCallback(async (opts: StartOptions = {}) => {
    setState({ partial: '', final: null, speechStopped: false, error: null, isStreaming: true });

    let token: string;
    try {
      const { data } = await axios.post(
        `${apiBaseUrl}/api/transcribe-stream/token`,
        {},
        opts.authHeader ? { headers: { Authorization: opts.authHeader } } : undefined,
      );
      token = data?.token;
      if (!token) throw new Error('No token returned from /token endpoint');
    } catch (e: any) {
      const err = new Error(`Failed to mint transcribe-stream token: ${e?.message || e}`);
      setState(s => ({ ...s, error: err, isStreaming: false }));
      throw err;
    }

    const wsBase = apiBaseUrl.replace(/^http/, 'ws');
    const ws = new WebSocket(`${wsBase}/api/transcribe-stream/ws?token=${encodeURIComponent(token)}`);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    const wsOpen = new Promise<void>((resolve, reject) => {
      ws.onopen = () => resolve();
      ws.onerror = () => reject(new Error('WS connection failed'));
    });

    ws.onmessage = (ev: MessageEvent) => {
      try {
        const msg = JSON.parse(typeof ev.data === 'string' ? ev.data : '');
        switch (msg.type) {
          case 'delta':
            setState(s => ({ ...s, partial: s.partial + (msg.text || '') }));
            break;
          case 'final':
            setState(s => ({ ...s, final: msg.text || '', isStreaming: false }));
            finalResolverRef.current?.(msg.text || '');
            finalResolverRef.current = null;
            finalRejecterRef.current = null;
            break;
          case 'speech_stopped':
            setState(s => ({ ...s, speechStopped: true }));
            break;
          case 'error':
            const err = new Error(`Transcribe stream error: ${msg.message || 'unknown'}`);
            setState(s => ({ ...s, error: err, isStreaming: false }));
            finalRejecterRef.current?.(err);
            finalResolverRef.current = null;
            finalRejecterRef.current = null;
            break;
        }
      } catch {
        // ignore non-JSON frames
      }
    };

    ws.onclose = () => {
      setState(s => ({ ...s, isStreaming: false }));
    };

    try {
      await wsOpen;
    } catch (e: any) {
      setState(s => ({ ...s, error: e, isStreaming: false }));
      await cleanup();
      throw e;
    }

    // Start PCM capture and pipe to WS
    try {
      recorderRef.current = await startPcmRecorder({
        onChunk: chunk => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(chunk);
          }
        },
        onError: err => {
          setState(s => ({ ...s, error: err, isStreaming: false }));
        },
      });
    } catch (e: any) {
      setState(s => ({ ...s, error: e, isStreaming: false }));
      await cleanup();
      throw e;
    }
  }, [cleanup]);

  /**
   * Send a manual STOP: tells the server to flush + commit and return a final
   * transcript. Resolves with the final text. Times out after `timeoutMs` and
   * rejects (caller can fall back to batch transcription).
   */
  const stopAndAwaitFinal = useCallback(
    (timeoutMs = 8000): Promise<string> => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        return Promise.reject(new Error('No active transcribe stream'));
      }

      // Stop capturing first, then ask server to commit.
      const finalPromise = new Promise<string>((resolve, reject) => {
        finalResolverRef.current = resolve;
        finalRejecterRef.current = reject;
      });
      const timer = setTimeout(() => {
        finalRejecterRef.current?.(new Error('Final transcript timeout'));
        finalResolverRef.current = null;
        finalRejecterRef.current = null;
      }, timeoutMs);

      void (async () => {
        try {
          await recorderRef.current?.stop();
        } catch {}
        recorderRef.current = null;
        try {
          ws.send(JSON.stringify({ type: 'commit' }));
        } catch {}
      })();

      return finalPromise.finally(() => clearTimeout(timer));
    },
    [],
  );

  useEffect(() => {
    return () => {
      void cleanup();
    };
  }, [cleanup]);

  return { ...state, start, stopAndAwaitFinal, cleanup };
}
